// Creature voxel prefabs — copied from adventure-craft.
// Voxel scale: 1 unit = 1/16 of a game unit (0.0625).
import type { BlockVoxel, CharacterPart, CharacterPrefab } from './types.js';

function box(
  sx: number,
  sy: number,
  sz: number,
  w: number,
  h: number,
  d: number,
  color: string,
): BlockVoxel[] {
  const out: BlockVoxel[] = [];
  for (let x = sx; x < sx + w; x++)
    for (let y = sy; y < sy + h; y++)
      for (let z = sz; z < sz + d; z++) out.push({ x, y, z, color });
  return out;
}

function humanoid(
  headColor: string,
  bodyColor: string,
  legColor: string,
  armColor: string,
  eyeColor: string,
  headH: number,
  bodyBase: number,
  bodyH: number,
  hasHelm?: boolean,
): CharacterPart[] {
  const hx = -2;
  const hy = bodyBase + bodyH;
  const hz = -1;
  const hw = 4;
  const hh = headH;
  const hd = 4;
  const head: BlockVoxel[] = [
    ...box(hx, hy, hz, hw, hh, hd, headColor),
    { x: -1, y: hy + 2, z: hz + hd, color: eyeColor },
    { x: 1, y: hy + 2, z: hz + hd, color: eyeColor },
  ];
  if (hasHelm) head.push(...box(-1, hy + hh, 0, 2, 2, 2, headColor));

  return [
    { name: 'head', voxels: head },
    { name: 'body', voxels: box(-2, bodyBase, 0, 4, bodyH, 2, bodyColor) },
    { name: 'left_arm', voxels: box(-4, bodyBase + 1, 0, 2, bodyH - 1, 2, armColor) },
    { name: 'right_arm', voxels: box(2, bodyBase + 1, 0, 2, bodyH - 1, 2, armColor) },
    { name: 'left_leg', voxels: box(-2, 0, 0, 2, bodyBase, 2, legColor) },
    { name: 'right_leg', voxels: box(0, 0, 0, 2, bodyBase, 2, legColor) },
  ];
}

// ─── Skeletons ───────────────────────────────────────────────
const skeleton: CharacterPrefab = {
  name: 'Skeleton',
  maxHp: 20,
  faction: 'undead',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-2, 10, -1, 4, 4, 4, '#e8e8e8'),
        { x: -1, y: 12, z: 3, color: '#1a1a1a' },
        { x: 1, y: 12, z: 3, color: '#1a1a1a' },
        { x: 0, y: 10, z: 3, color: '#1a1a1a' },
      ],
    },
    {
      name: 'body',
      voxels: [
        ...box(-2, 5, 0, 5, 4, 1, '#d4d4d4'),
        { x: 0, y: 9, z: 0, color: '#d4d4d4' },
        { x: 0, y: 8, z: 0, color: '#d4d4d4' },
        { x: 0, y: 7, z: 0, color: '#d4d4d4' },
        { x: 0, y: 6, z: 0, color: '#d4d4d4' },
        { x: 0, y: 5, z: 0, color: '#d4d4d4' },
      ],
    },
    { name: 'left_arm', voxels: box(-3, 6, 0, 1, 4, 1, '#d4d4d4') },
    { name: 'right_arm', voxels: box(3, 6, 0, 1, 4, 1, '#d4d4d4') },
    { name: 'left_leg', voxels: box(-1, 0, 0, 1, 5, 1, '#d4d4d4') },
    { name: 'right_leg', voxels: box(1, 0, 0, 1, 5, 1, '#d4d4d4') },
  ],
};

const zombie: CharacterPrefab = {
  name: 'Zombie',
  maxHp: 30,
  faction: 'undead',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-2, 10, -1, 4, 4, 4, '#5a7a5a'),
        { x: -1, y: 12, z: 3, color: '#1a1a1a' },
        { x: 1, y: 12, z: 3, color: '#1a1a1a' },
        { x: 0, y: 11, z: 3, color: '#3d5c3d' },
      ],
    },
    {
      name: 'body',
      voxels: [...box(-2, 5, 0, 4, 5, 2, '#4a6a4a'), ...box(-2, 5, 0, 4, 2, 2, '#3d5c3d')],
    },
    { name: 'left_arm', voxels: box(-4, 6, 0, 2, 5, 2, '#5a7a5a') },
    { name: 'right_arm', voxels: box(2, 6, 0, 2, 5, 2, '#5a7a5a') },
    { name: 'left_leg', voxels: box(-2, 0, 0, 2, 5, 2, '#3a4a6a') },
    { name: 'right_leg', voxels: box(0, 0, 0, 2, 5, 2, '#3a4a6a') },
  ],
};

const wraith: CharacterPrefab = {
  name: 'Wraith',
  maxHp: 30,
  faction: 'undead',
  canFly: true,
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-2, 12, -1, 4, 4, 4, '#1a1a3a'),
        { x: -1, y: 14, z: 3, color: '#4444ff' },
        { x: 1, y: 14, z: 3, color: '#4444ff' },
      ],
    },
    {
      name: 'body',
      voxels: [...box(-2, 6, 0, 4, 6, 2, '#0a0a2a'), ...box(-2, 4, -1, 4, 2, 0, '#0a0a2a')],
    },
    { name: 'left_arm', voxels: box(-4, 8, 0, 2, 4, 2, '#1a1a3a') },
    { name: 'right_arm', voxels: box(2, 8, 0, 2, 4, 2, '#1a1a3a') },
    { name: 'left_leg', voxels: box(-2, 2, 0, 2, 2, 2, '#0a0a2a') },
    { name: 'right_leg', voxels: box(0, 2, 0, 2, 2, 2, '#0a0a2a') },
  ],
};

const lich: CharacterPrefab = {
  name: 'Lich',
  maxHp: 200,
  faction: 'undead',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-2, 16, -2, 4, 4, 4, '#e8e8e8'),
        ...box(-3, 20, -2, 6, 2, 5, '#ffd700'),
        { x: -1, y: 18, z: 3, color: '#8b0000' },
        { x: 1, y: 18, z: 3, color: '#8b0000' },
      ],
    },
    {
      name: 'body',
      voxels: [...box(-4, 6, -2, 8, 10, 4, '#1a1a1a'), ...box(-3, 8, -3, 6, 8, 1, '#2e1065')],
    },
    {
      name: 'left_arm',
      voxels: [...box(-6, 12, 0, 2, 8, 2, '#1a1a1a'), ...box(-6, 10, 0, 2, 6, 2, '#e8e8e8')],
    },
    {
      name: 'right_arm',
      voxels: [
        ...box(4, 12, 0, 2, 8, 2, '#1a1a1a'),
        ...box(4, 10, 0, 2, 6, 2, '#e8e8e8'),
        ...box(5, 6, -2, 1, 14, 1, '#5c3317'),
        { x: 5, y: 20, z: -2, color: '#22c55e' },
      ],
    },
    { name: 'left_leg', voxels: box(-3, 0, 0, 3, 6, 3, '#1a1a1a') },
    { name: 'right_leg', voxels: box(0, 0, 0, 3, 6, 3, '#1a1a1a') },
  ],
};

const deathKnight: CharacterPrefab = {
  name: 'Death Knight',
  maxHp: 180,
  faction: 'undead',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-2, 14, -1, 4, 4, 4, '#1a1a1a'),
        { x: -1, y: 16, z: 3, color: '#4488ff' },
        { x: 1, y: 16, z: 3, color: '#4488ff' },
        ...box(-1, 18, 0, 2, 2, 2, '#2a2a2a'),
      ],
    },
    {
      name: 'body',
      voxels: [
        ...box(-3, 6, -1, 6, 8, 4, '#0a0a0a'),
        ...box(-2, 8, 3, 4, 4, 1, '#e8e8e8'),
        { x: 0, y: 10, z: 3, color: '#4488ff' },
      ],
    },
    { name: 'left_arm', voxels: box(-5, 8, 0, 2, 6, 2, '#1a1a1a') },
    {
      name: 'right_arm',
      voxels: [
        ...box(3, 8, 0, 2, 6, 2, '#1a1a1a'),
        ...box(4, 6, -2, 1, 10, 1, '#4488ff'),
        { x: 4, y: 16, z: -2, color: '#88ccff' },
      ],
    },
    { name: 'left_leg', voxels: box(-2, 0, 0, 2, 6, 2, '#0a0a0a') },
    { name: 'right_leg', voxels: box(0, 0, 0, 2, 6, 2, '#0a0a0a') },
  ],
};

// ─── Greenskins ──────────────────────────────────────────────
const orc: CharacterPrefab = {
  name: 'Orc',
  maxHp: 45,
  faction: 'greenskin',
  parts: humanoid('#2d5a1e', '#3d6a2e', '#2d4a1e', '#3d6a2e', '#ff4444', 4, 6, 5, true),
};

const ogre: CharacterPrefab = {
  name: 'Ogre',
  maxHp: 80,
  faction: 'greenskin',
  behavior: 'heavy',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-3, 16, -2, 6, 5, 5, '#6b8e5a'),
        { x: -2, y: 19, z: 3, color: '#ff4444' },
        { x: 2, y: 19, z: 3, color: '#ff4444' },
      ],
    },
    {
      name: 'body',
      voxels: [...box(-4, 7, -2, 8, 9, 5, '#5a7a4a'), ...box(-3, 9, -3, 6, 5, 1, '#4a6a3a')],
    },
    { name: 'left_arm', voxels: box(-7, 9, 0, 3, 8, 3, '#6b8e5a') },
    {
      name: 'right_arm',
      voxels: [
        ...box(4, 9, 0, 3, 8, 3, '#6b8e5a'),
        ...box(6, 7, -1, 2, 12, 2, '#5c3317'),
        { x: 7, y: 18, z: 0, color: '#8b4513' },
      ],
    },
    { name: 'left_leg', voxels: box(-3, 0, 0, 3, 7, 3, '#4a5a3a') },
    { name: 'right_leg', voxels: box(0, 0, 0, 3, 7, 3, '#4a5a3a') },
  ],
};

const goblin: CharacterPrefab = {
  name: 'Goblin',
  maxHp: 15,
  faction: 'greenskin',
  behavior: 'swarm',
  parts: humanoid('#4a7a2e', '#5a8a3e', '#3a5a1e', '#5a8a3e', '#ffcc00', 3, 4, 4),
};

// ─── Demons / Casters ────────────────────────────────────────
const sorcerer: CharacterPrefab = {
  name: 'Sorcerer',
  maxHp: 50,
  faction: 'demon',
  parts: humanoid('#2a1a3a', '#3a2a4a', '#2a1a3a', '#3a2a4a', '#ff00ff', 4, 6, 5),
};

// ─── Dragon ──────────────────────────────────────────────────
const dragon: CharacterPrefab = {
  name: 'Dragon',
  maxHp: 200,
  faction: 'boss',
  canFly: true,
  isBoss: true,
  skeletonType: 'quadruped',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-2, 14, 4, 4, 3, 6, '#8b0000'),
        ...box(-1, 14, 10, 2, 2, 2, '#5a0000'),
        ...box(-3, 14, -2, 6, 5, 6, '#a00000'),
        { x: -2, y: 17, z: 3, color: '#ffd700' },
        { x: 2, y: 17, z: 3, color: '#ffd700' },
        ...box(-4, 18, -1, 2, 4, 2, '#3a3a3a'),
        ...box(3, 18, -1, 2, 4, 2, '#3a3a3a'),
        ...box(-1, 19, -2, 2, 3, 4, '#5a0000'),
      ],
    },
    {
      name: 'body',
      voxels: [
        ...box(-4, 6, -4, 8, 8, 8, '#8b0000'),
        ...box(-3, 6, -3, 6, 6, 1, '#d4a574'),
        { x: 0, y: 14, z: -4, color: '#5a0000' },
        { x: 0, y: 13, z: -5, color: '#5a0000' },
        { x: 0, y: 12, z: -6, color: '#5a0000' },
      ],
    },
    {
      name: 'left_wing',
      voxels: [...box(-8, 10, -2, 4, 6, 8, '#700000'), ...box(-12, 10, 0, 4, 4, 6, '#600000')],
    },
    {
      name: 'right_wing',
      voxels: [...box(4, 10, -2, 4, 6, 8, '#700000'), ...box(8, 10, 0, 4, 4, 6, '#600000')],
    },
    {
      name: 'tail',
      voxels: [
        ...box(-2, 8, -8, 4, 4, 4, '#8b0000'),
        ...box(-1, 8, -12, 2, 3, 4, '#7a0000'),
        ...box(-1, 8, -16, 2, 2, 4, '#6a0000'),
        { x: 0, y: 9, z: -17, color: '#3a3a3a' },
      ],
    },
    {
      name: 'left_leg',
      voxels: [...box(-5, 0, -2, 3, 6, 4, '#700000'), ...box(-6, 0, 1, 1, 1, 2, '#3a3a3a')],
    },
    {
      name: 'right_leg',
      voxels: [...box(2, 0, -2, 3, 6, 4, '#700000'), ...box(5, 0, 1, 1, 1, 2, '#3a3a3a')],
    },
  ],
};

// ─── Bandits ─────────────────────────────────────────────────
const bandit: CharacterPrefab = {
  name: 'Bandit',
  maxHp: 25,
  faction: 'bandit',
  parts: humanoid('#c4956a', '#5a4a3a', '#3a2a1a', '#c4956a', '#1a1a1a', 4, 6, 5),
};

const warlord: CharacterPrefab = {
  name: 'Warlord',
  maxHp: 120,
  faction: 'bandit',
  isBoss: true,
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-3, 16, -2, 6, 5, 5, '#c4956a'),
        ...box(-3, 21, -2, 6, 2, 6, '#ffd700'),
        { x: -2, y: 19, z: 3, color: '#4444ff' },
        { x: 2, y: 19, z: 3, color: '#4444ff' },
      ],
    },
    {
      name: 'body',
      voxels: [...box(-4, 6, -1, 8, 10, 4, '#4a3a2a'), ...box(-5, 8, 0, 1, 4, 2, '#6a6a6a')],
    },
    { name: 'left_arm', voxels: box(-6, 8, 0, 2, 8, 2, '#c4956a') },
    {
      name: 'right_arm',
      voxels: [
        ...box(4, 8, 0, 2, 8, 2, '#c4956a'),
        ...box(5, 6, -1, 1, 12, 1, '#888888'),
        { x: 5, y: 18, z: -1, color: '#ffd700' },
      ],
    },
    { name: 'left_leg', voxels: box(-3, 0, 0, 3, 6, 3, '#3a2a1a') },
    { name: 'right_leg', voxels: box(0, 0, 0, 3, 6, 3, '#3a2a1a') },
  ],
};

// ─── Ranged / Special ────────────────────────────────────────
const skeletonArcher: CharacterPrefab = {
  name: 'Skeleton Archer',
  maxHp: 25,
  faction: 'undead',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-2, 10, -1, 4, 4, 4, '#e8e8e8'),
        { x: -1, y: 12, z: 3, color: '#ff4444' },
        { x: 1, y: 12, z: 3, color: '#ff4444' },
      ],
    },
    {
      name: 'body',
      voxels: [
        ...box(-2, 5, 0, 5, 4, 1, '#d4d4d4'),
        { x: 0, y: 9, z: 0, color: '#d4d4d4' },
        { x: 0, y: 8, z: 0, color: '#d4d4d4' },
        { x: 0, y: 7, z: 0, color: '#d4d4d4' },
        { x: 0, y: 6, z: 0, color: '#d4d4d4' },
        { x: 0, y: 5, z: 0, color: '#d4d4d4' },
      ],
    },
    {
      name: 'left_arm',
      voxels: [...box(-3, 8, 0, 1, 3, 1, '#d4d4d4'), ...box(-5, 7, -1, 4, 1, 1, '#8b4513')],
    },
    { name: 'right_arm', voxels: box(3, 6, 0, 1, 4, 1, '#d4d4d4') },
    { name: 'left_leg', voxels: box(-1, 0, 0, 1, 5, 1, '#d4d4d4') },
    { name: 'right_leg', voxels: box(1, 0, 0, 1, 5, 1, '#d4d4d4') },
  ],
};

const orcWolfRider: CharacterPrefab = {
  name: 'Orc Wolf Rider',
  maxHp: 65,
  faction: 'greenskin',
  skeletonType: 'quadruped',
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(0, 8, 8, 4, 3, 4, '#555555'),
        { x: 1, y: 9, z: 12, color: '#ff4444' },
        { x: 3, y: 9, z: 12, color: '#ff4444' },
        ...box(1, 10, 5, 2, 1, 4, '#444444'),
      ],
    },
    {
      name: 'body',
      voxels: [...box(-1, 6, 0, 6, 4, 10, '#555555'), ...box(0, 8, 2, 4, 3, 4, '#3d6a2e')],
    },
    { name: 'left_arm', voxels: box(4, 9, 3, 1, 2, 2, '#3d6a2e') },
    {
      name: 'right_arm',
      voxels: [...box(4, 9, 5, 1, 2, 2, '#3d6a2e'), ...box(5, 8, 5, 1, 4, 1, '#8b4513')],
    },
    { name: 'front_left_leg', voxels: box(1, 0, 2, 2, 6, 2, '#444444') },
    { name: 'front_right_leg', voxels: box(1, 0, 8, 2, 6, 2, '#444444') },
    { name: 'back_left_leg', voxels: box(1, 0, 0, 2, 6, 2, '#444444') },
    { name: 'back_right_leg', voxels: box(3, 0, 6, 2, 6, 2, '#444444') },
    {
      name: 'tail',
      voxels: [...box(2, 8, -4, 2, 1, 4, '#444444'), ...box(2, 7, -6, 1, 1, 2, '#444444')],
    },
  ],
};

const banshee: CharacterPrefab = {
  name: 'Banshee',
  maxHp: 60,
  faction: 'undead',
  canFly: true,
  parts: [
    {
      name: 'head',
      voxels: [
        ...box(-2, 14, -1, 4, 4, 4, '#c0c0c0'),
        { x: -1, y: 16, z: 3, color: '#ffffff' },
        { x: 1, y: 16, z: 3, color: '#ffffff' },
        { x: 0, y: 15, z: 3, color: '#000000' },
      ],
    },
    {
      name: 'body',
      voxels: [...box(-3, 6, 0, 6, 8, 2, '#d0d0d0'), ...box(-3, 4, -1, 6, 2, 0, '#d0d0d0')],
    },
    { name: 'left_arm', voxels: box(-5, 8, 0, 2, 6, 2, '#c0c0c0') },
    { name: 'right_arm', voxels: box(3, 8, 0, 2, 6, 2, '#c0c0c0') },
    { name: 'left_leg', voxels: box(-2, 2, 0, 2, 2, 2, '#d0d0d0') },
    { name: 'right_leg', voxels: box(0, 2, 0, 2, 2, 2, '#d0d0d0') },
  ],
};

// ─── NPCs ────────────────────────────────────────────────────
const wizard: CharacterPrefab = {
  name: 'Wizard',
  maxHp: 40,
  parts: humanoid('#c4956a', '#2a2a6a', '#1a1a3a', '#2a2a6a', '#8888ff', 5, 6, 5),
};

const ship = (
  hullColor: string,
  deckColor: string,
  sailColor: string,
  w: number,
  d: number,
  h: number,
): CharacterPart[] => {
  const parts: CharacterPart[] = [
    // Hull — tapered shape.
    {
      name: 'hull',
      voxels: box(-Math.floor(w / 2), 0, -Math.floor(d / 2), w, Math.ceil(h * 0.4), d, hullColor),
    },
    // Deck.
    {
      name: 'deck',
      voxels: box(
        -Math.floor(w * 0.4),
        Math.ceil(h * 0.4),
        -Math.floor(d * 0.3),
        Math.ceil(w * 0.8),
        1,
        Math.ceil(d * 0.6),
        deckColor,
      ),
    },
  ];
  // Mast + sail if sail color provided.
  if (sailColor) {
    const mx = 0;
    const my = Math.ceil(h * 0.4) + 1;
    const mz = 0;
    parts.push({
      name: 'mast',
      voxels: [
        { x: mx, y: my, z: mz, color: '#8b6914' },
        { x: mx, y: my + 1, z: mz, color: '#8b6914' },
        { x: mx, y: my + 2, z: mz, color: '#8b6914' },
        { x: mx, y: my + 3, z: mz, color: '#8b6914' },
      ],
    });
    parts.push({
      name: 'sail',
      voxels: [
        { x: mx - 1, y: my, z: mz, color: sailColor },
        { x: mx, y: my, z: mz, color: sailColor },
        { x: mx + 1, y: my, z: mz, color: sailColor },
        { x: mx - 1, y: my + 1, z: mz, color: sailColor },
        { x: mx, y: my + 1, z: mz, color: sailColor },
        { x: mx + 1, y: my + 1, z: mz, color: sailColor },
        { x: mx - 1, y: my + 2, z: mz, color: sailColor },
        { x: mx, y: my + 2, z: mz, color: sailColor },
        { x: mx + 1, y: my + 2, z: mz, color: sailColor },
      ],
    });
  }
  return parts;
};

const trade_ship: CharacterPrefab = {
  name: 'Trade Ship',
  maxHp: 60,
  parts: ship('#8b6914', '#c4a45a', '#f5f0e0', 6, 4, 4),
};

const transport_ship: CharacterPrefab = {
  name: 'Transport Ship',
  maxHp: 80,
  parts: ship('#6b4914', '#b8943a', '#e8dcc8', 7, 5, 4),
};

const warship: CharacterPrefab = {
  name: 'Warship',
  maxHp: 120,
  parts: ship('#4a3520', '#8b7355', '#cc3333', 6, 4, 5),
};

const galleon: CharacterPrefab = {
  name: 'Galleon',
  maxHp: 180,
  parts: ship('#3a2518', '#6b4c32', '#d4af37', 8, 6, 6),
};

export const creaturePrefabs: Record<string, CharacterPrefab> = {
  skeleton,
  zombie,
  wraith,
  lich,
  death_knight: deathKnight,
  orc,
  ogre,
  goblin,
  sorcerer,
  dragon,
  bandit,
  warlord,
  skeleton_archer: skeletonArcher,
  orc_wolf_rider: orcWolfRider,
  banshee,
  wizard,
  trade_ship,
  transport_ship,
  warship,
  galleon,
};
